import { useOutletContext } from 'react-router-dom'

import { Section } from '@factorialco/deprecated-design-system'
import { ProjectDetailsPageSettingsPageBudgetSectionFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ProjectBudgetForm, {
  IProjectBudgetValues,
} from 'modules/project_management/components/forms/ProjectBudgetForm'
import { useCreateFixedCostPlanning } from 'modules/project_management/hooks/planningMutations/useCreateFixedCostPlanning'
import { useCreateFixedTimeBudget } from 'modules/project_management/hooks/planningMutations/useCreateFixedTimeBudget'
import { useDeleteFixedCostPlanning } from 'modules/project_management/hooks/planningMutations/useDeleteFixedCostPlanning'
import { useDeleteFixedTimeBudget } from 'modules/project_management/hooks/planningMutations/useDeleteFixedTimeBudget'
import { useUpdateFixedCostPlanning } from 'modules/project_management/hooks/planningMutations/useUpdateFixedCostPlanning'
import { useUpdateFixedTimeBudget } from 'modules/project_management/hooks/planningMutations/useUpdateFixedTimeBudget'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import {
  getDecimalHoursFromMinutes,
  getMinutesFromDecimalHours,
} from 'modules/project_management/lib/formatHours'

export const FixedBudgetSection: React.FC<{
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
}> = ({ project }) => {
  const { company } = useCurrentSession()

  const { create: createFixedTimeBudget } = useCreateFixedTimeBudget(company.id)
  const { update: updateFixedTimeBudget } = useUpdateFixedTimeBudget()
  const { delete: deleteFixedTimeBudget } = useDeleteFixedTimeBudget()
  const { create: createFixedCostPlanning } = useCreateFixedCostPlanning(company.id)
  const { update: updateFixedCostPlanning } = useUpdateFixedCostPlanning()
  const { delete: deleteFixedCostPlanning } = useDeleteFixedCostPlanning()

  // Different options for mutation
  const getMutation = (data: IProjectBudgetValues): Promise<UnhandledResult<Result<unknown>>> => {
    // Update Fixed Time Budget
    if (data.budgetType === budgetTypesEnum.fixedTimeBudget && project.fixedTimeBudget) {
      return updateFixedTimeBudget({
        id: project.fixedTimeBudget.id,
        budgetAllocation: getMinutesFromDecimalHours(Number(data.budgetAllocation)),
      })
    }

    // Update cost planning
    if (data.budgetType === budgetTypesEnum.fixedCostPlanning && project.fixedCostPlanning) {
      return updateFixedCostPlanning({
        fixedCostPlanningId: project.fixedCostPlanning.id,
        budgetAllocationCents: Math.round(Number(data.budgetAllocationCents) * 100),
        feeAmountCents: data.feeAmountCents
          ? Math.round(Number(data.feeAmountCents) * 100)
          : undefined,
      })
    }

    // Delete Fixed Time Budget
    if (data.budgetType === budgetTypesEnum.withoutBudget && project.fixedTimeBudget) {
      return deleteFixedTimeBudget({ id: project.fixedTimeBudget.id })
    }

    // Delete Fixed Cost Planning
    if (data.budgetType === budgetTypesEnum.withoutBudget && project.fixedCostPlanning) {
      return deleteFixedCostPlanning({ id: project.fixedCostPlanning.id })
    }

    // Change from Fixed Cost Planning to Fixed Time Budget
    if (data.budgetType === budgetTypesEnum.fixedTimeBudget && project.fixedCostPlanning) {
      return createFixedTimeBudget({
        projectId: project.id,
        budgetAllocation: getMinutesFromDecimalHours(Number(data.budgetAllocation)),
      })
    }

    // Change from Fixed Time Budget to Fixed Cost Planning
    if (data.budgetType === budgetTypesEnum.fixedCostPlanning && project.fixedTimeBudget) {
      return createFixedCostPlanning({
        projectId: project.id,
        budgetAllocationCents: Math.round(Number(data.budgetAllocationCents) * 100),
        feeAmountCents: data.feeAmountCents
          ? Math.round(Number(data.feeAmountCents) * 100)
          : undefined,
      })
    }

    //Create Fixed Cost Planning
    if (
      data.budgetType === budgetTypesEnum.fixedCostPlanning &&
      !project.fixedCostPlanning &&
      !project.fixedTimeBudget
    ) {
      return createFixedCostPlanning({
        projectId: project.id,
        budgetAllocationCents: Math.round(Number(data.budgetAllocationCents) * 100),
        feeAmountCents: data.feeAmountCents
          ? Math.round(Number(data.feeAmountCents) * 100)
          : undefined,
      })
    }

    //Create Fixed Time Budget
    return createFixedTimeBudget({
      projectId: project.id,
      budgetAllocation: getMinutesFromDecimalHours(Number(data.budgetAllocation)),
    })
  }

  const { hasCostsFeature } = useOutletContext<{
    hasCostsFeature: boolean
  }>()

  const getProjectBudgetDefaultValues = () => {
    if (project.fixedTimeBudget) {
      return {
        budgetType: budgetTypesEnum.fixedTimeBudget,
        budgetAllocation: getDecimalHoursFromMinutes(project.fixedTimeBudget.budgetAllocation),
        budgetAllocationCents: null,
        feeAmountCents: null,
        currencyType: project.legalEntity.currency,
      }
    } else if (project.fixedCostPlanning) {
      return {
        budgetType: budgetTypesEnum.fixedCostPlanning,
        budgetAllocation: null,
        budgetAllocationCents: project.fixedCostPlanning.budgetAllocationCents / 100,
        feeAmountCents: project.fixedCostPlanning.feeAmountCents
          ? project.fixedCostPlanning.feeAmountCents / 100
          : undefined,
        currencyType: project.legalEntity.currency,
      }
    }
    return {
      budgetType: budgetTypesEnum.withoutBudget,
      budgetAllocation: undefined,
      budgetAllocationCents: undefined,
      feeAmountCents: undefined,
      currencyType: project.legalEntity.currency,
    }
  }

  const { values, errors, setValues, setErrors, handleSubmit, dirty } =
    useForm<IProjectBudgetValues>({
      defaultValues: getProjectBudgetDefaultValues(),
      enableReinitialize: true,
      validateOnChange: true,
      validateOnBlur: true,
      validate: (data) => {
        const errorValidation: FormErrors<IProjectBudgetValues> = {}

        if (
          data.budgetType === budgetTypesEnum.fixedTimeBudget &&
          (!data.budgetAllocation || data.budgetAllocation <= 0)
        )
          errorValidation.budgetAllocation = i18n.t(
            'project_management.planning.planned_hours_error'
          )

        if (data.budgetType === budgetTypesEnum.fixedCostPlanning) {
          if (!data.budgetAllocationCents) {
            errorValidation.budgetAllocationCents = i18n.t(
              'project_management.planning.validation_errors.field_is_mandatory'
            )
          } else {
            if (data.budgetAllocationCents <= 0) {
              errorValidation.budgetAllocationCents = i18n.t(
                'project_management.planning.validation_errors.field_higher_than_0'
              )
            }
            if (isNaN(data.budgetAllocationCents)) {
              errorValidation.budgetAllocationCents = i18n.t(
                'project_management.planning.validation_errors.field_is_number'
              )
            }
          }
        }
        if (data.budgetType === budgetTypesEnum.fixedCostPlanning && data.feeAmountCents) {
          if (data.feeAmountCents <= 0) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.planning.validation_errors.field_higher_than_0'
            )
          }
          if (isNaN(data.feeAmountCents)) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.planning.validation_errors.field_is_number'
            )
          }
        }

        return errorValidation
      },
      onSubmit: async (data) => {
        const result = await getMutation(data)

        const handled = handleResult(result, (err) => {
          if (err) setErrors(err)
        })

        if (handled.success) {
          if (data.budgetType === budgetTypesEnum.fixedTimeBudget) {
            if (project.fixedTimeBudget) {
              // Update fixed time budget
              track(trackingEvents.projectManagement.settings.updateBudget, {
                budgetType: 'fixed time budget',
                amountChanged: 'yes',
              })
            } else if (project.fixedCostPlanning) {
              // Change from cost to time
              track(trackingEvents.projectManagement.settings.updateBudget, {
                budgetType: 'fixed time budget',
              })
            } else {
              // Create fixed time budget
              track(trackingEvents.projectManagement.settings.addBudget, {
                budgetType: 'fixed time budget',
              })
            }
          }

          if (data.budgetType === budgetTypesEnum.fixedCostPlanning) {
            if (project.fixedCostPlanning) {
              // Update fixed cost planning
              track(trackingEvents.projectManagement.settings.updateBudget, {
                budgetType: project.isBillable ? 'Planned_cost_income' : 'Planned_cost',
                amountChanged:
                  data.budgetAllocationCents != project.fixedCostPlanning.budgetAllocationCents
                    ? 'yes'
                    : 'no',
                income: data.feeAmountCents ? 'yes' : 'no',
              })
            } else if (project.fixedTimeBudget) {
              // Change from time to cost
              track(trackingEvents.projectManagement.settings.updateBudget, {
                budgetType: project.isBillable ? 'Planned_cost_income' : 'Planned_cost',
                amountChanged: 'yes',
                income: data.feeAmountCents ? 'yes' : 'no',
              })
            } else {
              // Create fixed cost planning
              track(trackingEvents.projectManagement.settings.addBudget, {
                budgetType: project.isBillable ? 'Planned_cost_income' : 'Planned_cost',
              })
            }
          }

          if (data.budgetType === budgetTypesEnum.withoutBudget) {
            // Delete planning
            if (project.fixedCostPlanning || project.fixedTimeBudget) {
              track(trackingEvents.projectManagement.settings.deleteBudget)
            }
          }

          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      },
    })

  const handleFieldChanged = (
    fieldName: string,
    fieldValue: number | string | undefined,
    shouldValidate = false
  ) => {
    const newValues = { ...values, [fieldName]: fieldValue }

    if (fieldName === 'budgetType') {
      newValues['budgetAllocation'] = undefined
      newValues['budgetAllocationCents'] = undefined
      newValues['feeAmountCents'] = undefined

      setErrors({})
    }

    setValues(newValues, shouldValidate)
  }

  return (
    <Section.Form
      illustration="money"
      title={i18n.t('project_management.planning.title')}
      description={i18n.t('project_management.planning.description')}
      onSubmit={handleSubmit}
      submitLabel={i18n.t('common.submit')}
      isDirty={dirty}
      paddingX="s0"
    >
      <ProjectBudgetForm
        onChangeValue={handleFieldChanged}
        values={values}
        errors={errors}
        isBillable={project.isBillable || false}
        isOwner={!!project._policies?.assignOwner.can}
        hasCostsFeature={hasCostsFeature}
        hasBudgetFeature
      />
    </Section.Form>
  )
}
